import { FC, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { typedYupResolver } from 'features/common/utils/typedYupResolver';
import { buildPoolSchema } from 'features/common/utils/poolSchema';

import { IdParamsType } from 'utils/types';
import { getData, post, put } from 'services/api';
import { queryClient } from 'index';
import { Form } from 'features/common/components/Form';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupNumericInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupNumericInput';
import { PunchupTextArea } from 'features/common/components/Punchup/primitives/forms/fields/PunchupTextArea';
import { PunchupCheckbox } from 'features/common/components/Punchup/primitives/forms/fields/PunchupCheckbox';
import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';
import {
	useAccordionForm,
	useSeededEditForm,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { getPoolMemberLock, getPoolSoldFloor, PoolMemberTicketType } from 'features/common/utils/poolGuards';

import styles from './PoolForm.module.css';

interface PoolFormValues {
	name: string;
	description?: string;
	total_amount: number;
	ticket_type_ids: string[];
}

interface PoolDetailsApi extends PoolFormValues {
	ID: number;
	event_id: number;
	ticket_types?: { ID: number; name: string }[];
}

interface PoolFormProps {
	/** null → create mode; string → edit mode for this pool ID */
	poolId: string | null;
	onDone: () => void;
}

/**
 * PoolForm
 *
 * The pool create/edit form rendered inline inside the Pools accordion section. A pool is a
 * non-seated allocation bucket: a named capacity (`total_amount`) shared across one or more
 * ticket types. `poolId === null` → create; a string → edit. Pools are not used for seated
 * events — the section is not rendered when `event.seatsEventKey` is set.
 */
export const PoolForm: FC<PoolFormProps> = ({ poolId, onDone }) => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const isNewPool = poolId === null;

	// Read at validation time (not capture time) so the Lifetime-Σ floor stays current as the
	// member selection settles — see docs/pools_business_logic.md T-3.
	const soldFloorRef = useRef(0);

	// Schema rules live in ./utils (testable + single-sourced); the ref getter feeds the current floor.
	const schema = useMemo(() => buildPoolSchema(t, () => soldFloorRef.current), [t]);

	const formMethods = useForm<PoolFormValues>({
		resolver: typedYupResolver(schema),
		defaultValues: { name: '', description: '', total_amount: 0, ticket_type_ids: [] },
	});

	const { flash } = useAccordionForm(formMethods);
	const {
		formState: { isSubmitting, errors },
	} = formMethods;

	const selectedTicketTypeIds = (formMethods.watch('ticket_type_ids') ?? []) as string[];

	const eventTicketTypesQuery = useQuery(
		['event-ticket-types', eventId],
		async () => {
			const data = await getData(`events/${eventId}`);
			return data.ticket_types || [];
		},
		{ enabled: !!eventId }
	);

	const eventTicketTypes = (eventTicketTypesQuery.data ?? []) as PoolMemberTicketType[];
	// A pool can never be smaller than the tickets its members have already sold (T-3).
	const soldFloor = getPoolSoldFloor(selectedTicketTypeIds, eventTicketTypes);
	soldFloorRef.current = soldFloor;

	// Fetch the existing pool in edit mode and seed the form once from it, held behind a spinner
	// until then so the fields don't paint defaults and then re-seed (the blink). Seeding once also
	// keeps a later refetch from clobbering the user's in-progress edits.
	const { data: pool, isError: isPoolError } = useQuery<PoolDetailsApi>({
		queryKey: ['pool', eventId, poolId],
		queryFn: () => getData(`/events/${eventId}/pools/${poolId}`),
		enabled: !isNewPool && !!poolId,
	});

	const { isSeeded, seedGate } = useSeededEditForm<PoolDetailsApi, PoolFormValues>({
		isEditMode: !isNewPool,
		formMethods,
		seedData: pool,
		toFormValues: (data) => ({
			name: data.name,
			description: data.description || '',
			total_amount: data.total_amount,
			ticket_type_ids: data.ticket_types ? data.ticket_types.map((tt) => tt.ID.toString()) : [],
		}),
		isError: isPoolError,
	});

	const handleCheckboxChange = (id: string) => {
		const current = (formMethods.getValues('ticket_type_ids') ?? []) as string[];
		const newIds = current.includes(id) ? current.filter((tid) => tid !== id) : [...current, id];
		formMethods.setValue('ticket_type_ids', newIds, { shouldDirty: true });
	};

	const onSubmit = async (data: PoolFormValues) => {
		const payload = {
			...data,
			total_amount: Number(data.total_amount),
			ticket_type_ids: data.ticket_type_ids || [],
		};
		try {
			if (isNewPool) {
				await post(`/events/${eventId}/pools`, payload);
			} else {
				await put(`/events/${eventId}/pools/${poolId}`, payload);
			}
			queryClient.invalidateQueries({ queryKey: ['eventPools', eventId] });
			flash('success', { title: t('pool-saved', 'Pool saved'), onComplete: onDone });
		} catch {
			flash('error', { title: t('pool-save-failed', 'Failed to save pool') });
		}
	};

	// Edit mode is held behind the spinner until the saved pool is seeded (create renders immediately).
	if (!isSeeded) return <>{seedGate}</>;

	return (
		<Form formMethods={formMethods} onSubmit={onSubmit}>
			<div className={styles.formFields}>
				<PunchupInput
					id="name"
					name="name"
					label={t('poolDetails-name-label')}
					placeholder={t('poolDetails-name-placeholder')}
					required
				/>

				<PunchupTextArea
					id="description"
					name="description"
					label={t('poolDetails-description-label')}
					placeholder={t('poolDetails-description-placeholder')}
				/>

				<PunchupNumericInput
					id="total_amount"
					name="total_amount"
					unit="count"
					showStepper
					label={t('poolDetails-total-label')}
					placeholder={t('poolDetails-total-placeholder')}
					required
					help={
						t(
							'poolDetails-total-help',
							"The total number of tickets this pool can sell, shared across all the ticket types you attach to it. It can't be set below what's already sold."
						) +
						(soldFloor > 0
							? ' ' +
							  t('poolDetails-total-floor-hint', "{{count}} tickets are already sold, so the pool can't go lower.", {
									count: soldFloor,
							  })
							: '')
					}
					min={soldFloor}
				/>

				<div>
					<PunchupFieldLabel
						as="span"
						label={t('poolDetails-ticketTypes-label')}
						help={t(
							'poolDetails-ticketTypes-help',
							'Pick the ticket types that draw from this shared capacity. A ticket type can belong to only one pool at a time.'
						)}
					/>
					<div className={styles.checkboxList}>
						{eventTicketTypes.map((tt) => {
							const isSelected = selectedTicketTypeIds.includes(tt.ID.toString());
							const lock = getPoolMemberLock(tt, isSelected, poolId);
							const tooltip =
								lock === 'attached-elsewhere'
									? t('poolDetails-ticketType-attached-tooltip')
									: lock === 'selling-locked'
									? t('poolDetails-ticketType-selling-locked-tooltip')
									: lock === 'selling-add'
									? t('poolDetails-ticketType-selling-add-tooltip')
									: undefined;
							return (
								<PunchupCheckbox
									key={tt.ID}
									checked={isSelected}
									onChange={() => handleCheckboxChange(tt.ID.toString())}
									disabled={lock !== null}
									label={tt.name}
									title={tooltip}
								/>
							);
						})}
					</div>
					{errors.ticket_type_ids?.message && (
						<span className="punchupFormError">{errors.ticket_type_ids.message as string}</span>
					)}
				</div>
			</div>

			<div className="entityEditActions">
				<PunchupButton type="button" variant="secondary" text={t('button-cancel')} onClick={onDone} />
				<PunchupButton
					type="submit"
					variant="primary"
					disabled={isSubmitting}
					text={isNewPool ? t('button-create-new') : t('button-save')}
				/>
			</div>
		</Form>
	);
};
